// Libraries
import { CommonModule } from '@angular/common';
import { Component, effect, HostListener, inject, OnDestroy, OnInit, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addDays, endOfDay, isBefore, startOfDay } from 'date-fns';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

// Services
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Interfaces
import { Metric } from 'src/app/interfaces/api/metric';
import { Project } from 'src/app/interfaces/api/project';
import { Config } from '../../../interfaces/shared/config';
import { ExportProgress } from '../../../interfaces/shared/export-progress';

// Classes
import { GalleryTool } from 'src/app/classes/gallery/gallery.tools';
import { Tools } from 'src/app/classes/shared/tools';

// UI
import { ButtonComponent, DatePickerComponent, KeyValue, RangePickerComponent } from 'ui-library';

// Components
import { ListModeComponent } from 'src/app/components/project-pages/risks/list-mode/list-mode.component';
import { MapModeComponent } from 'src/app/components/project-pages/risks/map-mode/map-mode.component';

@Component({
  selector: 'app-risks',
  imports: [
    ButtonComponent,
    MatButtonModule,
    MatToolbarModule,
    MatIconModule,
    TranslateModule,
    CommonModule,
    MatCheckboxModule,
    FormsModule,
    GoogleMapsModule,
    MatDividerModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatSelectModule,
    MatNativeDateModule,
    DatePickerComponent,
    RangePickerComponent,
    ListModeComponent,
    MapModeComponent,
    MatMenuModule,
    MatSlideToggleModule
  ],
  templateUrl: './risks.component.html',
  styleUrls: ['./risks.component.scss'],
  standalone: true,
})

export class RisksComponent implements OnInit, OnDestroy {
  // Services
  private readonly authService: AuthService = inject(AuthService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly metricService: MetricService = inject(MetricService);
  private readonly userService: UserService = inject(UserService);
  private readonly unitService: UnitService = inject(UnitService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly galleryTool: GalleryTool = inject(GalleryTool);
  readonly tools: Tools = inject(Tools);

  // Component API
  @ViewChild(MapModeComponent) mapModeComponent!: MapModeComponent;
  @ViewChild(ListModeComponent) listModeComponent!: ListModeComponent;

  // Variables
  project!: Project;
  projectId: any = "";
  modelId: string = "";
  colors = Config.colors;
  markers: any = {};
  mapOptions: any;
  destroy$ = new Subject<void>();
  playerValueSbj!: BehaviorSubject<number>;
  playerValue!: Observable<any>;
  startDate: string = startOfDay(new Date()).toISOString();
  endDate: string = endOfDay(new Date()).toISOString();
  viewerPositions = [];
  mapObject = {};
  showMap = true;
  projectXY = [0, 0];
  risks: Metric[] = [];
  filteredRisks: any[] = [];
  risks$: BehaviorSubject<Metric[]> = new BehaviorSubject<Metric[]>([]);
  zoneList: KeyValue[] = [];
  sequenceList: KeyValue[] = [];
  riskList: KeyValue[] = [];
  criticityList: KeyValue[] = [];
  selectedCriticity: string[] = [];
  showEndDate = false;
  currentRisk: any;
  imageAngle: number = 0;
  icons: any;
  showFilter: string = "";
  riskFilters: any = {
    risk: [
      "EPI non conformes",
      "Gestes et postures",
      "Basculement Renversement Effondrement",
      "Perforation ",
      "Coactivité",
      "Chute d'objets",
      "Equipements de travail",
      "Co-activité Engins Piétons",
      "Enfouissement",
      "Manutention manuelle "
    ],
    criticity: [],
    zone: [],
    sequence: []
  }
  risksDisplay: Record<string, boolean> = {};
  riskFilters$: BehaviorSubject<any> = new BehaviorSubject<any>(this.riskFilters);
  riskButtonText: string = '';
  zoneButtonText: string = '';
  sequenceButtonText: string = '';
  loading: boolean = true;
  loadingPDF = false;
  exportCurrent = 0;
  exportTotal = 0;
  trackers: any[] = [];
  units: any[] = [];
  selectedUnits: string[] = [];
  mode = "map";
  switchedMode: boolean = false;
  mapLoaded: boolean = false;
  intervalId: any;

  // Signals
  readonly activitySignal: WritableSignal<any> = signal(null);
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
  readonly currentUser: Signal<any> = this.authService.currentUser;

  constructor() {
    let startDate = this.route.snapshot.queryParamMap.get('startDate')
    let endDate = this.route.snapshot.queryParamMap.get('endDate')
    let showEndDate = this.route.snapshot.queryParamMap.get('showEndDate');
    let mode = this.route.snapshot.queryParamMap.get("mode");

    if (startDate) {
      this.startDate = startDate;
    }

    if (endDate) {
      this.endDate = endDate;
    }

    if (showEndDate)
      this.showEndDate = showEndDate.toLowerCase() == "true";

    if (mode && ["map", "list", "commented_list", "invalidated_list"].includes(mode)) {
      this.mode = mode;
    }

    this.projectId = this.route.snapshot.queryParamMap.get('project');
    this.loading = true;

    if (this.projectId) {
      this.projectService.getProjectById(this.projectId).pipe(takeUntil(this.destroy$)).subscribe((project: any) => {
        this.project = project;

        this.projectService.getStatus(project.database).pipe(takeUntil(this.destroy$)).subscribe(result => {
          this.project.status = result[0];
          this.projectService.setCurrentProject(project);
        });

        this._loadActivity(project.database);
        this.projectService.setCurrentProject(project);

        this.userService.setCurrentProject(this.projectId).pipe(takeUntil(this.destroy$)).subscribe(async (user: any) => {
          this.unitService.getUnits().pipe(take(1)).subscribe((units: any[]) => {
            this.units = units
          });

          this.viewerPositions = this.currentUser().viewerPositions;

          if (this.project.enableTags) {
            this.project.enableTags.filter(t => t.module == "risk").forEach(t => {
              this.risksDisplay[t.name] = t.enabled
            });
          }

          if (!this.modelId || this.modelId != this.project.model) {
            this.modelId = this.project.model as string;
          }

          await this.getRisks();
        });

      })
    } else {
      // Effect: Change project
      effect(() => {
        const project = this.currentProject();
        this._loadActivity(project.database);

        this.unitService.getUnits().pipe(take(1)).subscribe(async (units: any[]) => {
          this.units = units;

          if (this.project && this.project.id !== project.id) {
            this.project = project;
            this.ngOnInit()
          } else {
            this.project = project;
          }

          if (this.project.enableTags) {
            this.project.enableTags.filter((t: any) => t.module == "risk").forEach((t: any) => {
              this.risksDisplay[t.name] = t.enabled
            });
          }
          if (!this.modelId || this.modelId != this.project.model) {
            this.modelId = this.project.model as string;
          }

          await this.getRisks();
        });
      })

      if (this.currentUser())
        this.viewerPositions = this.currentUser().viewerPositions;
    }

    effect(async () => {
      let tagNames = this.galleryTool.tagNames();

      if (tagNames && tagNames["risk"]?.length)
        this.riskFilters.risk = tagNames["risk"];
    });
  }

  ngOnInit(): void {
    this.risks = [];
    this.loading = true;
    this.mapLoaded = false;
    this.currentRisk = null;
    this.risks$.next([]);
  }

  @HostListener("click", ["$event"])
  onMouseClick($event: MouseEvent) {
    const target = $event.target as HTMLElement;
    if (target && (target.closest('.filter-container'))) {
      return;
    }
    if (this.showFilter)
      this.showFilter = "";
  }

  async getRisks() {
    this.loading = true;
    this.currentRisk = null;
    this.risks = [];

    const query: any = {
      startDate: this.startDate,
      endDate: this.endDate,
      name: "Risque",
      isCommented: this.mode == 'commented_list' ? true : undefined,
      notClear: this.mode == 'invalidated_list',
      limit: 10000
    }

    const prevRiskValues: Record<string, KeyValue['value']> = Object.fromEntries(this.riskList.map(i => [i.key, i.value]));
    const prevZoneValues: Record<string, KeyValue['value']> = Object.fromEntries(this.zoneList.map(i => [i.key, i.value]));
    const prevSequenceValues: Record<string, KeyValue['value']> = Object.fromEntries(this.sequenceList.map(i => [i.key, i.value]));

    this.riskList = [];
    this.zoneList = [];
    this.sequenceList = [];
    this.criticityList = [];

    this.riskFilters$.subscribe(filters => {
      this.filteredRisks = this.risks.filter(risk => {
        let riskFilter = risk.payload.title ? filters.riskList.find((i: KeyValue) => i.key == risk.payload.title && i.value) : true;
        // let criticityFilter = risk.payload.criticity ? this.riskFilters.criticity.includes(risk.payload.criticity) : true;
        let zoneFilter = risk.payload.zone ? filters.zoneList.find((i: KeyValue) => i.key == risk.payload.zone && i.value) : true;
        let sequenceFilter = risk.payload.sequence ? filters.sequenceList.find((i: KeyValue) => i.key == risk.payload.sequence && i.value) : true;
        return riskFilter && zoneFilter && sequenceFilter;
      })
    })

    this.metricService.getMetrics(query).pipe(takeUntil(this.destroy$)).subscribe(risks => {
      if (risks && risks.length) {
        this.risks = risks;
        if (this.risks.filter(risk => risk.payload.lat && risk.payload.lon).length == 0 && this.mode == "map") {
          this.setMode("list", false);
        }

        const addToFilter = (key: string, value: string, list: KeyValue[], risk: Metric, prevValues: Record<string, KeyValue['value']> = {}) => {
          const currValue = risk.payload[value];
          if (currValue && !list.find(i => i.key === currValue)) {
            const hasPrev = currValue in prevValues && prevValues[currValue] !== 'disable';
            const itemValue: KeyValue['value'] = (hasPrev ? prevValues[currValue] : this.risksDisplay?.[currValue]) ?? value;
            if (!this.riskFilters[key].includes(currValue) && (!hasPrev || itemValue)) {
              this.riskFilters[key].push(currValue);
            }
            list.push({ key: currValue, value: itemValue });
          }
        }

        for (const m of this.risks) {
          try {
            addToFilter("risk", "title", this.riskList, m, prevRiskValues);
            addToFilter("zone", "zone", this.zoneList, m, prevZoneValues);
            addToFilter("sequence", "sequence", this.sequenceList, m, prevSequenceValues);
            addToFilter("criticity", "criticity", this.criticityList, m);
            let { zone = "", sequence = "" } = m["payload"]
            if (["Non défini", "Not defined"].includes(zone))
              m.payload.zone = "";
            if (["Non défini", "Not defined"].includes(sequence!))
              m.payload.sequence = "";
          } catch (error) {
            console.error(error);
          }
        }

        this.onFilterChange();
        this.loading = false;
      } else {
        this.loading = false;
        this.currentRisk = null;
        // Restore previous filter lists so selections survive a mode with no data
        this.riskList = Object.entries(prevRiskValues).map(([key, value]) => ({ key, value }));
        this.zoneList = Object.entries(prevZoneValues).map(([key, value]) => ({ key, value }));
        this.sequenceList = Object.entries(prevSequenceValues).map(([key, value]) => ({ key, value }));
      }
      this.risks$.next([...this.risks]);

      this.riskFilters.risk.forEach((i: string) => {
        let index = this.riskList.findIndex(l => l.key == i);
        if (index == -1) this.riskList.push({ key: i, value: "disable" })
      })
      this.riskFilters.zone.findIndex((i: string) => {
        let index = this.zoneList.findIndex(l => l.key == i);
        if (index == -1) this.zoneList.push({ key: i, value: "disable" })
      })
      this.riskFilters.sequence.findIndex((i: string) => {
        let index = this.sequenceList.findIndex(l => l.key == i);
        if (index == -1) this.sequenceList.push({ key: i, value: "disable" })
      })
      this.onFilterChange();
    })
  }

  setRiskVisibility(metricId: string, visible: boolean) {
    let risk = this.risks.find(m => m.id == metricId);
    if (risk)
      risk.visible = visible;
  }

  onFilterChange() {
    this.riskButtonText = this.translate.instant('risks.list') + ' ' + this.riskList.filter(l => l.value).length + '/' + this.riskList.length;
    this.zoneButtonText = this.translate.instant('risks.zone') + ' ' + this.zoneList.filter(l => l.value).length + '/' + this.zoneList.length;
    this.sequenceButtonText = this.translate.instant('risks.sequence') + ' ' + this.sequenceList.filter(l => l.value).length + '/' + this.sequenceList.length;
    this.riskFilters$.next({ sequenceList: this.sequenceList, selectedUnits: this.selectedUnits, zoneList: this.zoneList, riskList: this.riskList });
  }

  navigateDate(offset: number) {
    const currentDate = new Date();

    const newStartDate = addDays(new Date(this.startDate), offset);
    const newEndDate = addDays(new Date(this.endDate), offset);

    if (isBefore(newStartDate, currentDate) || isBefore(newEndDate, currentDate)) {
      this.startDate = newStartDate.toISOString();
      this.endDate = newEndDate.toISOString();

    }

    this.setRouterParams();

    this.getRisks()

  }

  setRouterParams() {
    const queryParams: Params = { startDate: this.startDate, endDate: this.endDate, mode: this.mode };

    if (this.showEndDate)
      queryParams['showEndDate'] = this.showEndDate;
    else
      queryParams['showEndDate'] = null;

    this.router.navigate(
      [],
      {
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      });
  }


  onShowFilter(filter: string) {
    if (filter == this.showFilter) {
      this.showFilter = "";
    } else {
      this.showFilter = filter;
    }
    if (!this.showFilter) {
      this.riskFilters = {
        risk: this.riskList.filter(l => l.value).map(l => l.key),
        criticity: this.criticityList.filter(l => l.value).map(l => l.key),
        zone: this.zoneList.filter(l => l.value).map(l => l.key),
        sequence: this.sequenceList.filter(l => l.value).map(l => l.key)
      }
    }
  }

  onPickerChanged(date: any) {
    this.startDate = startOfDay(new Date(date)).toISOString();
    this.endDate = endOfDay(new Date(date)).toISOString();
    this.navigateDate(0);
  }

  onRangePickerChanged(event: any) {
    this.startDate = startOfDay(new Date(event.startDate)).toISOString();
    this.endDate = endOfDay(new Date(event.endDate)).toISOString();
    this.navigateDate(0);
  }

  onShowEndChange() {
    this.showEndDate = !this.showEndDate;
    if (!this.startDate || !this.endDate) return;
    if (!this.showEndDate)
      this.endDate = endOfDay(new Date(this.startDate)).toISOString();

    this.navigateDate(0);
  }

  filterByTracker(event: MatSelectChange) {
    this.onFilterChange();
  }

  setMode(mode: string, fetch: boolean = true) {
    this.mode = mode;
    this.setRouterParams();
    if (fetch) {
      this.getRisks();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getRisksObs(): Observable<any[]> {
    return this.risks$.asObservable();
  }

  getRiskFiltersObs(): Observable<any[]> {
    return this.riskFilters$.asObservable();
  }

  async exportPDF() {
    this.loadingPDF = true;
    await new Promise(res => setTimeout(res, 300));
    let geolocatedRisk = this.risks.find(m => m.payload.lat && m.payload.lon);
    if (this.mode == "map" && this.mapModeComponent)
      this.mapModeComponent.exportPDF();
    else if (this.mode == "list" && this.listModeComponent) { // (!geolocatedRisk)
      this.listModeComponent.exportPDF();
    } else if (this.mode != "map") {
      this.mode = "map";
      this.mapLoaded = false;
      this.intervalId = setInterval(() => {
        if (this.mapLoaded) {
          this.switchedMode = true;
          this.mapModeComponent.exportPDF();
          clearInterval(this.intervalId);
        }
      }, 100);
    }
  }

  onExport(data: ExportProgress) {
    this.loadingPDF = data.loading;
    this.exportCurrent = data.current ?? 0;
    this.exportTotal = data.total ?? 0;
    if (!this.loading && this.switchedMode) {
      this.mode = "list";
      this.switchedMode = false;
    }
  }

  onMapLoaded(loaded: boolean) {
    this.mapLoaded = loaded;
  }

  getDisplayName(title: string) {
    return this.galleryTool.getDisplayName(title, "risk");
  }

  /**
 * Load activity for risks and set it to the activity signal
 */
  private _loadActivity(database: string): void {
    this.activitySignal.set(null);

    this.projectService.getActivity({ databases: [database], name: 'Risque' })
      .pipe(takeUntil(this.destroy$))
      .subscribe((activity: any) => {
        this.activitySignal.set(activity);
      });
  }

  // addPage(pageTitle = "", add = true) {
  //   try {
  //     let lang = this.lang;
  //     this.lastY = this.headerHeight;
  //     if (!this.title)
  //       this.title = pageTitle;
  //     this.currentPage++;
  //     if (add)
  //       this.pdf.addPage();

  //     this.addBg();
  //     // this.pdf.addImage(this.bg, 'JPG', 0, 0, this.width, this.height);

  //     //project name
  //     this.pdf.setTextColor(this.textColor);
  //     let projectFontSize = 42;
  //     this.pdf.setFontSize(projectFontSize);

  //     // In case project name too large
  //     let projectName = this.projectName;
  //     if (projectName.length > 53) {
  //       projectName = projectName.substring(0, 50).trim() + "...";
  //     }

  //     this.pdf.text(projectName, this.extMargin, projectFontSize);

  //     //----------

  //     //tab
  //     this.pdf.setTextColor(this.subTitleColor);
  //     let tabFontSize = 48;
  //     this.pdf.setFontSize(tabFontSize);
  //     // In case title too large
  //     if (this.title.length > 48) {
  //       this.title = this.title.substring(0, 45).trim() + "...";
  //     }

  //     this.pdf.text(this.title, this.extMargin, projectFontSize + tabFontSize);

  //     //----------

  //     //info
  //     this.pdf.setTextColor(this.textColor)
  //     let infoFontSize = 48;
  //     this.pdf.setFontSize(infoFontSize);

  //     let dateText = `${moment().format(locales[lang].timeFormat)}`;
  //     if (this.startDate && this.endDate) {
  //       dateText = `${locales[lang].from} ${moment(this.startDate).format(locales[lang].timeFormat)} ${locales[lang].to} ${moment(this.endDate).format(locales[lang].timeFormat)}`
  //     }

  //     this.pdf.text(dateText,
  //       this.width - this.extMargin, infoFontSize + this.extMargin * .5, { align: "right" });

  //     //----------

  //     //title report
  //     this.pdf.setTextColor(this.infoColor)

  //     this.pdf.setFontSize(36);
  //     this.pdf.text(locales[lang].customTitle, this.extMargin, this.height - this.extMargin);

  //     //----------

  //     //page number
  //     this.pdf.setTextColor(this.textColor)

  //     this.pdf.setFontSize(36);
  //     this.pdf.text(this.currentPage.toString(), this.width * .5, this.height - this.extMargin, { align: "center" });

  //     //----------

  //   } catch (e) {
  //     console.error(e);
  //   }
  // }
}
